export interface IActivationDrawerMenuItem {
  id: number;
  name: string;
  path: string;
}

/**
 * Legacy single-method payment shape.
 *
 * Kept while payment method selection is behind a flag: both flows coexist
 * until phase 2. Superseded by `IActivationPaymentFormValues` (form state) and
 * `ActivationPaymentPayload` (wire payload).
 */
export interface IActivationPayment {
  accountHolderName: string;
  bankAccountNumber: string;
  authorizeAllsetra?: boolean;
  termsAndConditions?: boolean;
  privacyPolicy?: boolean;
  directDebit?: boolean;
}

// ─────────────────────────── Payment method codes ───────────────────────────

export const ACTIVATION_PAYMENT_METHOD = {
  AUTOMATIC_COLLECTION: "AUTOMATIC_COLLECTION",
  MANUAL_BANK_TRANSFER: "MANUAL_BANK_TRANSFER",
  DIRECT_PAYMENT: "DIRECT_PAYMENT"
} as const;

export type ActivationPaymentMethodCode =
  (typeof ACTIVATION_PAYMENT_METHOD)[keyof typeof ACTIVATION_PAYMENT_METHOD];

/**
 * Codes this client knows how to render. The catalog comes from the API, but
 * each method still needs a component that knows which fields it collects, so a
 * code we do not implement is dropped rather than rendered blank.
 */
export const SUPPORTED_PAYMENT_METHOD_CODES: ReadonlyArray<string> =
  Object.values(ACTIVATION_PAYMENT_METHOD);

// ─────────────────────────── Method catalog ───────────────────────────

export interface IPaymentMethodDto {
  /** Raw code from the API. May be a value this client does not implement. */
  code: string;
  displayName: string;
  description?: string;
  requiresIban: boolean;
  requiresMandateConsent: boolean;
  /**
   * `null` means no country restriction.
   * An empty array means no country qualifies — not a synonym for `null`.
   */
  supportedIbanCountries: string[] | null;
  /**
   * Countries the method is withheld from, applied after
   * `supportedIbanCountries`. `null` or absent means nothing is excluded.
   *
   * This is how the manual-transfer rule is expressed: available everywhere
   * except where automatic collection already covers the customer. Without it
   * the alternative is listing thirty-odd SEPA countries, or having the client
   * infer one method's availability from another's — a business rule in the
   * wrong layer.
   */
  excludedIbanCountries?: string[] | null;
  isEnabled: boolean;
  sortOrder?: number;
}

export interface IPaymentMethodsResponse {
  methods: IPaymentMethodDto[];
}

/** A catalog entry narrowed to a code this client implements. */
export interface IActivationPaymentMethod
  extends Omit<IPaymentMethodDto, "code"> {
  code: ActivationPaymentMethodCode;
}

// ─────────────────────── Payment form state (flat) ───────────────────────

/**
 * Formik does not work well with discriminated unions, so form state stays one
 * flat shape and the union is built at submit time by
 * `toActivationPaymentPayload`.
 */
export interface IActivationPaymentFormValues {
  paymentMethod: ActivationPaymentMethodCode | "";
  accountHolderName: string;
  bankAccountNumber: string;
  authorizeAllsetra: boolean;
  directDebit: boolean;
  termsAndConditions: boolean;
  privacyPolicy: boolean;
}

// ─────────────────────── Payment wire payload (union) ───────────────────────

interface IActivationPaymentPayloadBase {
  accountHolderName: string;
  bankAccountNumber: string;
  termsAndConditions: boolean;
  privacyPolicy: boolean;
}

export interface IAutomaticCollectionPayment
  extends IActivationPaymentPayloadBase {
  paymentMethod: typeof ACTIVATION_PAYMENT_METHOD.AUTOMATIC_COLLECTION;
  authorizeAllsetra: boolean;
  directDebit: boolean;
}

export interface IManualBankTransferPayment
  extends IActivationPaymentPayloadBase {
  paymentMethod: typeof ACTIVATION_PAYMENT_METHOD.MANUAL_BANK_TRANSFER;
}

export interface IDirectPaymentPayment extends IActivationPaymentPayloadBase {
  paymentMethod: typeof ACTIVATION_PAYMENT_METHOD.DIRECT_PAYMENT;
}

/**
 * Mandate consent only means anything for automatic collection. Methods that do
 * not use it omit the flags entirely rather than sending `false`, so the API is
 * never invited to branch on a field that does not apply.
 */
export type ActivationPaymentPayload =
  | IAutomaticCollectionPayment
  | IManualBankTransferPayment
  | IDirectPaymentPayment;

// ─────────────────────────── Slice state ───────────────────────────

export interface IActivationPaymentState {
  methodsCatalog: IActivationPaymentMethod[];
  methodsCatalogLoading: boolean;
  methodsCatalogError: string | null;
  selectedPaymentMethod: ActivationPaymentMethodCode | "";
}

export interface IAlaramContactPersons {
  prefix: string;
  firstName: string;
  lastName: string;
  phoneNumber1: string;
  countryCode1: string;
  phoneNumber2: string;
  countryCode2: string;
  email: string;
  safeQuestionId: string;
  safeQuestionAnswer: string;
}
export interface IActivationInformation {
  companyInfo?: {
    name: string;
    street: string;
    number: string;
    extension?: string;
    postCode: string;
    city: string;
    country: string;
    countryId: number;
    vatNumber: string;
  };
  userInfo?: {
    prefix: string;
    firstName: string;
    lastName: string;
    phoneNumber1: string;
    countryCode1: string;
    phoneNumber2: string;
    countryCode2: string;
    email: string;
    safeQuestionId: string;
    safeQuestionAnswer: string;

    countryId: number;
  };
  alarmPersons?: Array<IAlaramContactPersons>;
  accountId?: string;
}

export interface IAccountInformation {
  accountId: string;
}

export interface IActivationPortal
  extends IActivationPayment,
    IActivationInformation {
  subscriptionId: string;
  tabSelectedIndex?: number;
  /**
   * Optional because the legacy single-method flow never sets it; present once
   * payment method selection is enabled.
   */
  paymentMethod?: ActivationPaymentMethodCode | "";
}
export interface IActivationPortalInitialValues {
  initialValues: IActivationPortal;
  order: number;
}
